import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Radar, RefreshCw, Briefcase } from "lucide-react";
import { useRedirect } from "../../../Shared/Hooks/useRedirect ";
import { storage } from "../../../Shared/Utils/authStorage";
import { EMPTY_DASHBOARD, CandidateDashboard, CandidateDashboardFilter } from "../Types/CandidateIntelligenceTypes";
import { getCandidateKpis, searchCandidateDashboard } from "../Services/CandidateIntelligenceService";
import { FilterBar } from "../Components/FilterBar";
import { KpiGrid } from "../Components/KpiGrid";
import { ProfileIntelligenceSection } from "../Components/ProfileIntelligenceSection";
import { OpportunitiesSection } from "../Components/OpportunitiesSection";
import { DiscoverySection } from "../Components/DiscoverySection";
import { InvitationCenter } from "../Components/InvitationCenter";
import {
  AchievementsIntelligenceCard,
  CareerIntelligenceCard,
  ImpactIntelligenceCard,
  LeadershipIntelligenceCard,
  SkillsIntelligenceCard,
} from "../Components/IntelligenceCards";
import { TimelineSection } from "../Components/TimelineSection";
import CandidateDashboardHeader from "../../Components/CandidateDashboardHeader";
import { CandidateProfile } from "../../Types/CandidateType";
import { getCandidateById } from "../../Services/CandidateService";

const DEFAULT_FILTER: CandidateDashboardFilter = {
  dateRange: "30",
  roles: [],
  locations: [],
  workModes: [],
  jobTypes: [],
  keyword: "",
};

const sectionIds = {
  profile: "command-profile",
  opportunities: "command-opportunities",
  discovery: "command-discovery",
  invitations: "command-invitations",
  intelligence: "command-intelligence",
  timeline: "command-timeline",
};

export const  CandidateCommandCenterPage: React.FC = () => {
  const redirect = useRedirect();
  const [filter, setFilter] = useState<CandidateDashboardFilter>(DEFAULT_FILTER);
  const [candidate, setCandidate] = useState<CandidateProfile | null>(null);
  

  // Core data — loaded from the aggregate endpoint
  const [dashboard, setDashboard] = useState<CandidateDashboard>(EMPTY_DASHBOARD);
  const [kpis, setKpis] = useState(EMPTY_DASHBOARD.kpis);
  const [mainLoading, setMainLoading] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<boolean>(true);

  const [refreshKey, setRefreshKey] = useState(0);
  const debounceRef = useRef<number | null>(null);

  const loadCore = useCallback(async (currentFilter: CandidateDashboardFilter) => {
    setMainLoading(true);
    setError("");
    const userId = storage.getUserId();
    const filterWithUser = { ...currentFilter, ...(userId ? { userId } : {}) };
    try {
      const [searchRes, kpiRes] = await Promise.all([
        searchCandidateDashboard(filterWithUser),
        getCandidateKpis(filterWithUser),
      ]);
      if (searchRes.success && searchRes.data) setDashboard(searchRes.data);
      else if (searchRes.message) setError(searchRes.message);
      if (kpiRes.success && kpiRes.data) setKpis(kpiRes.data);
    } catch (err) {
      setError("Dashboard could not be loaded. Please try again later.");
    } finally {
      setMainLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCore(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {    
    debounceRef.current = window.setTimeout(() => {
      void loadCore(filter);
    }, 450);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onFilterChange = useCallback((next: CandidateDashboardFilter) => setFilter(next), []);
  const onRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const generated = useMemo(
    () =>
      dashboard.generatedAtEpochMilli
        ? new Date(dashboard.generatedAtEpochMilli).toLocaleTimeString()
        : "generating…",
    [dashboard.generatedAtEpochMilli]
  );



  useEffect(() => {
    const fetchCandidate = async () => {
      // 1. Get the ID inside the effect to ensure fresh data
      const userId = storage.getUserId();
  
  
      // 2. Guard Clause: Don't fetch if there is no ID
      if (!userId) {
        setError("No user ID found. Please log in again.");
        setLoading(false);
        return;
      }
  
      try {
        setLoading(true); // Ensure loading is true when starting
        const response = await getCandidateById(userId);

        if (response.success) {
          setCandidate(response.data);
        } else {
          setError(response.message || "Could not retrieve candidate profile.");
        }
      } catch (err) {
        setError("An unexpected error occurred while loading the profile.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCandidate();
  }, []); // Empty dependency array is fine if using localStorage

  return (
    <div className="min-h-screen bg-slate-950 p-4 pb-16 text-slate-100 md:p-6 xl:p-8">

<CandidateDashboardHeader candidate={candidate}/>

      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {dashboard.profilePhotoUrl ? (
              <img
                src={dashboard.profilePhotoUrl}
                alt="profile"
                className="h-16 w-16 rounded-2xl border border-cyan-500/30 object-cover"
              />
            ) : (
              <span className="grid h-16 w-16 place-items-center rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 text-2xl font-black text-cyan-300">
                {dashboard.fullName?.charAt(0) ?? "P"}
              </span>
            )}
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-400">
                <Radar size={14} /> Candidate Command Center
              </p>
              <h1 className="mt-1 text-2xl font-black md:text-3xl">
                {dashboard.greeting}, {dashboard.fullName ?? "there"}
              </h1>
              {dashboard.headline ? <p className="mt-0.5 text-sm text-slate-400">{dashboard.headline}</p> : null}
              <p className="mt-1 text-[11px] text-slate-500">
                {dashboard.dateRangeApplied} window · generated {generated}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => redirect("/candidate/edit-profile")}
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-cyan-500/50"
            >
              Edit profile
            </button>
            <button
              type="button"
              onClick={() => redirect("/candidate/clasic")}
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-cyan-500/50"
            >
              Classic dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Filter bar */}
      <div className="mb-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
          <Briefcase size={12} className="text-cyan-400" /> Filters
        </div>
        <div className="mt-2">
          <FilterBar filter={filter} onFilterChange={onFilterChange} onRefresh={onRefresh} loading={mainLoading} />
        </div>
      </div>

      {error ? (
        <div className="mb-6 flex items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200">
          <AlertTriangle size={16} /> {error}
        </div>
      ) : null}

      {/* KPI cards */}
      <div id={sectionIds.profile} className="mb-8 scroll-mt-6">
        <KpiGrid
          kpis={kpis}
          onDrill={(target) => {
            if (target === "completion") scrollToId(sectionIds.profile);
            if (target === "matches") scrollToId(sectionIds.opportunities);
            if (target === "views" || target === "interest") scrollToId(sectionIds.discovery);
            if (target === "invitations") scrollToId(sectionIds.invitations);
            if (target === "visibility") scrollToId(sectionIds.discovery);
          }}
        />
      </div>

      {/* Profile intelligence / signals / improvement */}
      <div className="mb-8">
        <ProfileIntelligenceSection
          profile={dashboard.profileIntelligence}
          signals={dashboard.profileQualitySignals}
          improvement={dashboard.improvementCenter}
        />
      </div>

      {/* Opportunities */}
      <div id={sectionIds.opportunities} className="mb-8 scroll-mt-6">
        <OpportunitiesSection opportunities={dashboard.opportunities} />
      </div>

      {/* Discovery analytics + recruiter activity */}
      <div id={sectionIds.discovery} className="mb-8 scroll-mt-6">
        <DiscoverySection discovery={dashboard.discovery} recruiters={dashboard.recruiterActivity} />
      </div>

      {/* Invitation center */}
      <div id={sectionIds.invitations} className="mb-8 scroll-mt-6">
        <InvitationCenter refreshKey={refreshKey} onStatusChanged={onRefresh} />
      </div>

      {/* Intelligence fold */}
      <div id={sectionIds.intelligence} className="mb-8 scroll-mt-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-100">Deeper intelligence</h2>
          <span className="text-[11px] text-slate-500">Career · Leadership · Skills · Impact · Achievements</span>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          <CareerIntelligenceCard data={dashboard.careerIntelligence} />
          <LeadershipIntelligenceCard data={dashboard.leadershipIntelligence} />
          <SkillsIntelligenceCard data={dashboard.skillsIntelligence} />
          <ImpactIntelligenceCard data={dashboard.impactIntelligence} />
          <AchievementsIntelligenceCard data={dashboard.achievementsIntelligence} />
          <SectionProgress data={dashboard} loading={mainLoading} />
        </div>
      </div>

      {/* Timeline + next steps */}
      <div id={sectionIds.timeline} className="scroll-mt-6">
        <TimelineSection events={dashboard.activity} steps={dashboard.nextSteps} />
      </div>
    </div>
  );
};

const scrollToId = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Auxiliary card so the intelligence grid stays balanced on wide screens. */
const SectionProgress: React.FC<{ data: CandidateDashboard; loading: boolean }> = ({ data, loading }) => (
  <div className="relative flex flex-col justify-between rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-5">
    {loading ? (
      <span className="flex items-center gap-2 text-xs text-slate-500">
        <RefreshCw size={14} className="animate-spin" /> Refreshing intelligence…
      </span>
    ) : (
      <>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Signal summary</p>
          <p className="mt-2 text-3xl font-black text-cyan-300">{data.kpis.visibility}%</p>
          <p className="text-xs text-slate-400">overall recruiter-visibility score</p>
        </div>
        <ul className="mt-4 flex flex-col gap-1.5 text-xs text-slate-300">
          <li className="flex items-center justify-between border-b border-slate-800 pb-1.5">
            <span>Profile completion</span>
            <span className="font-black text-emerald-300">{data.kpis.completion}%</span>
          </li>
          <li className="flex items-center justify-between border-b border-slate-800 pb-1.5">
            <span>All-time invitations</span>
            <span className="font-black text-indigo-300">{data.kpis.invitations}</span>
          </li>
          <li className="flex items-center justify-between border-b border-slate-800 pb-1.5">
            <span>Matching roles</span>
            <span className="font-black text-amber-300">{data.kpis.matches}</span>
          </li>
          <li className="flex items-center justify-between">
            <span>Discovery events</span>
            <span className="font-black text-fuchsia-300">{data.discovery.length}</span>
          </li>
        </ul>
      </>
    )}
  </div>
);